import {
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Optional,
  Output,
  Self,
  ViewContainerRef,
  type ComponentRef,
} from '@angular/core';
import {FormGroupDirective, NgControl} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

import {ControlErrorComponent} from '@shared/components/control-error/control-error.component';
import {FORM_ERRORS} from '../services/error-handler';
import {placeFormErrorElement, setFormControlInvalidClass} from './place-form-error.util';

@UntilDestroy()
@Directive({
  selector: '[appFormError]',
  standalone: true,
})
export class FormErrorDirective {
  text!: string;
  compRef!: ComponentRef<ControlErrorComponent> | undefined;
  @Input() autoGeneratedError: boolean = true;
  @Input() appendedInput: boolean = false;
  @Input() needSpace: boolean = true;
  @Output() errorStatusChanged: EventEmitter<string | null> = new EventEmitter<string | null>();
  @Input() moreCheckRequired: boolean = false;

  constructor(
    @Self() private control: NgControl,
    @Inject(FORM_ERRORS) private errors: any,
    private elementRef: ElementRef,
    private vcr: ViewContainerRef,
    @Optional() private formGroupDirective: FormGroupDirective | null,
  ) {}

  removeError() {
    if (!this.compRef) {
      return;
    }

    const hostEl = this.compRef.location?.nativeElement as HTMLElement | undefined;
    if (hostEl?.parentNode) {
      hostEl.parentNode.removeChild(hostEl);
    }
    this.compRef.destroy();
    this.compRef = undefined;
  }

  setError(text: string) {
    if (!this.autoGeneratedError) {
      return;
    }

    if (!this.compRef) {
      this.compRef = this.vcr.createComponent(ControlErrorComponent);
    }

    this.compRef.instance.needSpace = this.needSpace;
    this.compRef.instance.setError(text);
    this.compRef.changeDetectorRef.detectChanges();
    this.placeErrorOutsideInputGroup();
  }

  setErrorStyleForInput() {
    setFormControlInvalidClass(this.elementRef.nativeElement as HTMLElement, true);
  }

  removeErrorStyleForInput() {
    setFormControlInvalidClass(this.elementRef.nativeElement as HTMLElement, false);
  }

  private destroyed = false;
  private errorStateQueued = false;

  ngOnInit(): void {
    const syncErrorState = () => {
      const controlErrors = this.control.errors;
      const submitted = !!this.formGroupDirective?.submitted;
      const shouldShow =
        !!controlErrors && (!!this.control.dirty || submitted);

      if (shouldShow) {
        const firstKey = Object.keys(controlErrors)[0];
        const getError = this.errors[firstKey];
        const text = getError ? getError(controlErrors[firstKey]) : firstKey;
        this.formInvalid(text);
      } else {
        this.formValid();
      }
    };

    // Defer past FormGroupDirective.resetForm(): it clears `submitted` only after form.reset().
    const queueSyncErrorState = () => {
      if (this.errorStateQueued) {
        return;
      }

      this.errorStateQueued = true;
      queueMicrotask(() => {
        this.errorStateQueued = false;
        if (!this.destroyed) {
          syncErrorState();
        }
      });
    };

    this.control.valueChanges?.pipe(untilDestroyed(this)).subscribe(queueSyncErrorState);

    this.control.statusChanges?.pipe(untilDestroyed(this)).subscribe(queueSyncErrorState);

    this.formGroupDirective?.ngSubmit.pipe(untilDestroyed(this)).subscribe(queueSyncErrorState);

    queueSyncErrorState();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  private placeErrorOutsideInputGroup(): void {
    placeFormErrorElement(
      this.compRef?.location?.nativeElement as HTMLElement | undefined,
      this.elementRef.nativeElement as HTMLElement,
      {appendedInput: this.appendedInput},
    );
  }

  private formInvalid(text: any) {
    this.errorStatusChanged.emit(text ?? null);
    this.setErrorStyleForInput();
    if (text) {
      this.setError(text);
    } else {
      this.removeError();
    }
  }

  private formValid() {
    this.errorStatusChanged.emit(null);
    this.removeErrorStyleForInput();
    this.removeError();
  }
}
